import React from 'react'
import PropTypes from 'prop-types'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Label,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
// } from 'recharts/umd/Recharts'

// @material-ui
import {
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
  Checkbox,
  FormControlLabel,
  withWidth,
} from '@material-ui/core'

// core
import { withLang } from '_core/hocs/withLang'
import { getFormattedValue } from '_core/utils/formatPrice'
import { State } from '_core/components/State'
import { Loading } from '_core/components/Loading'
import { SelectInput } from '_core/components/SelectInput'

// style
import { useCurrencyChartStyle } from './currencyChartStyle'

export const RawCurrencyChartView = props => {
  const {
    isXs,
    codes,
    rates,
    range,
    ranges,
    source,
    filter,
    yAxisWidth,
    nodeKey,
    isLoading,
    lineColors,
    currencies,
    defaultMargin,
    formatTick,
    getSequence,
    onSelectChange,
    chartRef,
    selectLabelRef,
    getOnFilterClick,
    getLangString: l,
  } = props

  const classes = useCurrencyChartStyle()

  const renderLegend = (rates, source) => (
    <Grid container={true} className={classes.filter}>
      {rates
        .filter(rate => `${rate.id}` !== `${source}`)
        .map(rate => (
          <Grid key={rate.id} item={true} xs={4} sm={2}>
            <FormControlLabel
              disabled={isLoading}
              checked={!filter[rate.id]}
              control={
                <Checkbox
                  size="small"
                  className={classes.checkbox}
                  onClick={getOnFilterClick(rate.id)}
                />
              }
              label={
                <Typography
                  display="inline"
                  variant="overline"
                  className={classes.label}
                  style={
                    !isLoading ? { color: lineColors[rate.id] } : undefined
                  }
                >
                  {codes[rate.id]}
                </Typography>
              }
            />
          </Grid>
        ))}
    </Grid>
  )

  const renderLines = (rates, source) =>
    rates
      .filter(rate => `${rate.id}` !== `${source}`)
      .map(rate => (
        <Line
          key={rate.id}
          dataKey={rate.id}
          hide={filter[rate.id]}
          stroke={lineColors[rate.id]}
          isAnimationActive={false}
          activeDot={{ r: 6 }}
        />
      ))

  const renderTooltip = ({ active, payload, label }) => {
    if (!active) {
      return null
    }

    const rowProps = {
      display: 'inline',
      variant: 'caption',
      color: 'textPrimary',
    }

    return (
      <Card className={classes.tooltip}>
        <Typography variant="caption" className={classes.tooltipTitle}>
          {label}
        </Typography>

        <Divider />

        <List
          dense={true}
          disablePadding={true}
          className={classes.tooltipList}
        >
          {payload.map(item => {
            const value = getFormattedValue(
              item.value,
              currencies.find(currency => `${currency.id}` === `${item.name}`)
            )

            return (
              <ListItem
                key={item.name}
                disableGutters={true}
                className={classes.tooltipItem}
              >
                <ListItemText
                  primary={`${codes[item.name]}:`}
                  secondary={value}
                  primaryTypographyProps={{
                    ...rowProps,
                    className: classes.tooltipName,
                  }}
                  secondaryTypographyProps={{
                    ...rowProps,
                    className: classes.tooltipValue,
                  }}
                />
              </ListItem>
            )
          })}
        </List>
      </Card>
    )
  }

  return (
    <Card className={classes.root}>
      <CardContent className={classes.content}>
        <SelectInput
          label={l('r._.currencychart.label.range')}
          value={range}
          options={ranges}
          onChange={onSelectChange}
          disabled={isLoading}
          helperText=""
          labelProps={{ ref: selectLabelRef }}
        />

        <div className={classes.chart}>
          <State
            key={nodeKey}
            waitProps={{ source }}
            getProps={{ sequence: getSequence }}
            progress={<Loading progressType="circular" />}
            render={({ sequence, source }) => (
              <div className={classes.chartWrap} ref={chartRef}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={sequence}
                    margin={!isXs ? { left: defaultMargin } : undefined}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <Tooltip content={renderTooltip} />
                    <XAxis dataKey="time" />

                    <YAxis
                      hide={isXs}
                      width={yAxisWidth}
                      type="number"
                      domain={['auto', 'auto']}
                      tickFormatter={formatTick}
                    >
                      <Label
                        angle={-90}
                        position="left"
                        value={codes[source].toUpperCase()}
                      />
                    </YAxis>

                    {renderLines(rates, source)}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          />
        </div>

        {renderLegend(rates, source)}
      </CardContent>
    </Card>
  )
}

RawCurrencyChartView.defaultProps = {}

RawCurrencyChartView.propTypes = {
  // self props
  yAxisWidth: PropTypes.number,
  isXs: PropTypes.bool.isRequired,
  codes: PropTypes.object.isRequired,
  range: PropTypes.string.isRequired,
  source: PropTypes.string.isRequired,
  filter: PropTypes.object.isRequired,
  defaultMargin: PropTypes.number.isRequired,
  nodeKey: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  lineColors: PropTypes.object.isRequired,
  formatTick: PropTypes.func.isRequired,
  getSequence: PropTypes.func.isRequired,
  onSelectChange: PropTypes.func.isRequired,
  getOnFilterClick: PropTypes.func.isRequired,

  chartRef: PropTypes.shape({
    current: PropTypes.any,
  }).isRequired,

  selectLabelRef: PropTypes.shape({
    current: PropTypes.any,
  }).isRequired,

  rates: PropTypes.arrayOf(
    PropTypes.shape({
      time: PropTypes.object,
      value: PropTypes.number,
    })
  ),

  ranges: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      data: PropTypes.shape({
        limit: PropTypes.number,
        interval: PropTypes.string,
      }),
    })
  ).isRequired,

  currencies: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      scale: PropTypes.number,
      code: PropTypes.string,
      title: PropTypes.string,
      format: PropTypes.string,
      user_visible: PropTypes.bool,
      switch_visible: PropTypes.bool,
    })
  ),
}

export const CurrencyChartView = withLang(withWidth()(RawCurrencyChartView))
